import { useAppearance } from "@/hooks/use-appearance"
import { CircleAlert, CircleCheck, CircleX, Info } from "lucide-react"
import { Toaster as Sonner, ToasterProps } from "sonner"

// Fluent 2 toast styling. Per the Fluent 2 spec a toast is a NEUTRAL surface
// (white / dark card) with a status-COLORED icon — never a full color fill. So
// `richColors` is off by default and each intent carries a filled circular icon
// (green check, red X, amber !, blue i). A dismiss (X) button is shown; app.css
// repositions it to the top-right to match Fluent's anatomy. The theme follows
// the app's own appearance system (which toggles the `.dark` class), NOT
// next-themes. Individual mounts may still override via props.
const iconClass = "size-5 shrink-0"

const Toaster = ({ position = "top-center", richColors = false, ...props }: ToasterProps) => {
  const { appearance } = useAppearance()

  return (
    <Sonner
      theme={appearance as ToasterProps["theme"]}
      className="toaster group"
      position={position}
      richColors={richColors}
      closeButton
      icons={{
        success: <CircleCheck className={`${iconClass} fill-success text-success-foreground`} />,
        error: <CircleX className={`${iconClass} fill-danger text-danger-foreground`} />,
        warning: <CircleAlert className={`${iconClass} fill-warning text-warning-foreground`} />,
        info: <Info className={`${iconClass} fill-info text-info-foreground`} />,
      }}
      toastOptions={{
        classNames: {
          toast:
            "group toast gap-3 rounded-xl border border-border bg-popover p-4 text-popover-foreground shadow-lg",
          title: "text-sm font-semibold text-foreground",
          description: "text-sm text-muted-foreground",
          actionButton:
            "rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground",
          cancelButton:
            "rounded-md bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground",
        },
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

export { Toaster }
